import {
  applyRenataFieldToForm,
  consumeRenataPendingPrompt,
} from "@/lib/renata/workspace-focus";
import { useState, useRef, useEffect, useCallback } from "react";
import {
  Brain,
  Send,
  Square,
  Mic,
  Paperclip,
  Loader2,
  X,
  StopCircle,
  Volume2,
  VolumeX,
  Camera,
  Upload,
  Lightbulb,
  AlertTriangle,
  ChevronRight,
  Radio,
  UserCheck,
  ClipboardCheck,
  FileSearch,
} from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RenataMessageBubble } from "./RenataMessage";
import { RenataAvatar } from "./RenataAvatar";
import type { RenataMessage, RenataProposal, RenataConversation } from "@/lib/renata/types";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { clientRequestHeaders } from "@/lib/sm-api";

interface RenataChatProps {
  tenantId: string;
  conversation: RenataConversation;
  initialMessages?: RenataMessage[];
  context?: Record<string, unknown>;
  className?: string;
  /** Deep link ?task= — start task orchestrator on mount. */
  initialTaskId?: string | null;
  /** Show handoff button (Transferir a humano). */
  showHandoff?: boolean;
}

interface PendingFile {
  file: File;
  name: string;
}

const FILE_ACCEPT = ".xlsx,.xls,.csv,.txt,.pdf,image/*,audio/*,.docx";

function isAllowedFile(f: File): boolean {
  const allowed = ["image/", "application/pdf", "text/", "audio/", "application/vnd", "application/octet-stream"];
  if (allowed.some((prefix) => f.type.startsWith(prefix))) return true;
  const name = f.name.toLowerCase();
  return /\.(xlsx|xls|csv|txt|pdf|docx|png|jpe?g|gif|webp|webm|mp3|wav|m4a)$/.test(name);
}

export function RenataChat({
  tenantId,
  conversation,
  initialMessages = [],
  context = {},
  className,
  initialTaskId = null,
  showHandoff = false,
}: RenataChatProps) {
  const [messages, setMessages] = useState<RenataMessage[]>(initialMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(false);
  /** listening | transcribing | thinking | speaking | idle */
  const [voicePhase, setVoicePhase] = useState<"idle" | "listening" | "transcribing" | "thinking" | "speaking">("idle");
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [memoryBanner, setMemoryBanner] = useState<string | null>(null);
  const [proactiveInsights, setProactiveInsights] = useState<Array<{ id: string; title: string; severity: string; summary: string }>>([]);
  const [handoffStatus, setHandoffStatus] = useState<"ai" | "waiting_human" | "human" | "resumed_ai">("ai");
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [lastCaptureDocs, setLastCaptureDocs] = useState<Array<{
    id: string; docClass: string; confidence: string; needsReview: boolean; filename: string;
  }>>([]);
  const navigate = useNavigate();
  const voiceModeRef = useRef(false);
  const lastUserMsgRef = useRef("");
  const abortControllerRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxPx = Math.round(window.innerHeight * 0.4);
    const next = el.scrollHeight;
    el.style.height = `${Math.max(40, Math.min(next, maxPx))}px`;
    el.style.overflowY = next > maxPx ? "auto" : "hidden";
  }

  useEffect(() => {
    resizeTextarea();
  }, [input]);
  const dragCounterRef = useRef(0);
  const taskStartedRef = useRef(false);

  const contextIntent =
    typeof context.intent === "string"
      ? context.intent
      : typeof (context.draft as { intent?: string } | undefined)?.intent === "string"
        ? (context.draft as { intent: string }).intent
        : null;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ?task= deep link — start task orchestrator once conversation is ready
  useEffect(() => {
    if (!initialTaskId || taskStartedRef.current) return;
    taskStartedRef.current = true;

    void (async () => {
      try {
        const res = await fetch("/api/renata/tasks", {
          method: "POST",
          credentials: "include",
          headers: {
            ...clientRequestHeaders(tenantId),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            conversationId: conversation.id,
            taskId: initialTaskId,
            message: "",
          }),
        });
        const data = (await res.json()) as {
          kind?: string;
          question?: string;
          summary?: string;
          proposal?: RenataProposal;
          error?: string;
        };
        if (!res.ok || data.kind === "none") {
          if (data.error) toast.error(data.error);
          return;
        }

        const assistantMsg: RenataMessage = {
          id: crypto.randomUUID(),
          conversation_id: conversation.id,
          tenant_id: tenantId,
          role: "assistant",
          content:
            data.kind === "ask"
              ? (data.question ?? "Contame más para continuar con la tarea.")
              : (data.summary ?? "Tengo una propuesta lista para tu revisión."),
          attachments: [],
          skill_used: initialTaskId,
          proposals: data.kind === "propose" && data.proposal ? [data.proposal] : [],
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);

        if (typeof window !== "undefined" && window.location.pathname.startsWith("/app/renata")) {
          const url = new URL(window.location.href);
          url.searchParams.delete("task");
          window.history.replaceState({}, "", url.toString());
        }
      } catch {
        toast.error("No se pudo iniciar la tarea.");
      }
    })();
  }, [conversation.id, initialTaskId, tenantId]);

  // Load proactive insights for empty state
  useEffect(() => {
    if (messages.length > 0) return;
    void fetch("/api/renata/insights?proactive=1", {
      credentials: "include",
      headers: clientRequestHeaders(tenantId),
    })
      .then((r) => r.json())
      .then((d: { insights?: Array<{ id: string; title: string; severity: string; summary: string }> }) => {
        setProactiveInsights(d.insights ?? []);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const addPendingFiles = useCallback((files: File[]) => {
    const valid = files.filter(isAllowedFile);
    if (valid.length !== files.length) toast.warning("Algunos archivos no están soportados.");
    if (valid.length === 0) return;
    setPendingFiles((prev) => [...prev, ...valid.map((f) => ({ file: f, name: f.name }))]);
    // Analytics: attachment_uploaded
    void fetch("/api/renata/channel-events", {
      method: "POST",
      credentials: "include",
      headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
      body: JSON.stringify({
        eventType: "attachment_uploaded",
        conversationId: conversation.id,
        meta: { count: valid.length },
      }),
    }).catch(() => {});
  }, [tenantId, conversation.id]);

  // Clipboard paste listener
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        addPendingFiles(files);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [addPendingFiles]);

  function cancelGeneration() {
    if (!abortControllerRef.current) return;
    abortControllerRef.current.abort();
    abortControllerRef.current = null;
    setLoading(false);
    setVoicePhase(voiceModeRef.current ? "listening" : "idle");
    toast.message("Generación cancelada");
  }

  const sendMessage = useCallback(
    async (messageText?: string, extraFiles?: File[]) => {
      const text = messageText ?? input.trim();
      const files = extraFiles ?? pendingFiles.map((f) => f.file);
      if (!text && files.length === 0) return;

      // Track last user message for Regenerar
      if (text) lastUserMsgRef.current = text;

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setLoading(true);
      const optimisticId = crypto.randomUUID();
      const optimisticMsg: RenataMessage = {
        id: optimisticId,
        conversation_id: conversation.id,
        tenant_id: tenantId,
        role: "user",
        content: text,
        attachments: [],
        skill_used: null,
        proposals: [],
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticMsg]);
      setInput("");
      setPendingFiles([]);

      try {
        const formData = new FormData();
        formData.append("message", text);
        formData.append("conversationId", conversation.id);
        formData.append("context", JSON.stringify(context));
        for (const f of files) formData.append("file", f);

        const res = await fetch("/api/renata/chat", {
          method: "POST",
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
          body: formData,
          signal: abortController.signal,
        });

        if (!res.ok) throw new Error("Error al enviar mensaje");
        const data = (await res.json()) as {
          userMessage: RenataMessage;
          assistantMessage: RenataMessage;
          memoryHints?: Array<{ kind: string; text: string }>;
          memoryUsedIds?: string[];
          fieldDraft?: { field: string; value: string } | null;
          captureDocuments?: Array<{
            id: string; docClass: string; confidence: string; needsReview: boolean; filename: string;
          }>;
        };

        if (data.captureDocuments?.length) {
          setLastCaptureDocs(data.captureDocuments);
          // Si hay exactamente uno y es factura/remito/orden_compra, ir directo al review
          const doc = data.captureDocuments[0];
          if (
            data.captureDocuments.length === 1 &&
            doc &&
            ["factura", "remito", "orden_compra"].includes(doc.docClass)
          ) {
            void navigate({ to: `/app/renata/capture/${doc.id}` });
            return;
          }
        }

        setMessages((prev) =>
          prev
            .filter((m) => m.id !== optimisticId)
            .concat([data.userMessage, data.assistantMessage]),
        );

        // Auto-aplicar solo si Renata generó texto de campo y el usuario pedía editar texto.
        const intent =
          typeof context.intent === "string" ? context.intent : "";
        const msgAsksField =
          /descripci[oó]n|mejor(ar|á|a)?\b|reescrib|ampli|escrib|gener(ar|á)|redact|hac[eé]\s+la\b/i.test(
            text,
          );
        const shouldAutoApply =
          Boolean(context.field) ||
          msgAsksField ||
          /write_|improve_|rewrite|expand|summarize|translate|descripcion|description/i.test(
            intent,
          );
        if (shouldAutoApply) {
          const fromApi = data.fieldDraft;
          const fromProposal = (data.assistantMessage?.proposals ?? []).find(
            (p) => p.skill_id === "inline_field" || p.action === "apply_field",
          );
          const field =
            fromApi?.field ||
            (typeof fromProposal?.params?.field === "string"
              ? fromProposal.params.field
              : typeof context.field === "string"
                ? context.field
                : "");
          const value =
            fromApi?.value ||
            (typeof fromProposal?.params?.after === "string"
              ? fromProposal.params.after
              : typeof fromProposal?.preview === "string"
                ? fromProposal.preview
                : "");
          if (field && value && value.trim().length >= 20) {
            applyRenataFieldToForm({ field, value });
          }
        }

        const hintText = data.memoryHints?.map((h) => h.text).filter(Boolean).join(" ") ?? "";
        setMemoryBanner(hintText || (data.memoryUsedIds?.length ? "Renata usó memoria operativa en esta respuesta." : null));

        if ((ttsEnabled || isVoiceMode) && data.assistantMessage?.content) {
          if (isVoiceMode) setVoicePhase("speaking");
          await playTTS(data.assistantMessage.content, tenantId);
          if (voiceModeRef.current) {
            void startVoiceModeRecording();
          } else {
            setVoicePhase("idle");
          }
        } else if (voiceModeRef.current) {
          void startVoiceModeRecording();
        } else {
          setVoicePhase("idle");
        }
      } catch (err) {
        const aborted =
          (err instanceof DOMException && err.name === "AbortError") ||
          (err instanceof Error && err.name === "AbortError");
        if (aborted) {
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setVoicePhase(voiceModeRef.current ? "listening" : "idle");
        } else {
          toast.error("No se pudo enviar el mensaje. Intentá de nuevo.");
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setInput(text);
          setVoicePhase(voiceModeRef.current ? "listening" : "idle");
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        setLoading(false);
      }
    },
    [input, pendingFiles, conversation.id, tenantId, context, ttsEnabled, isVoiceMode],
  );

  // Launcher "Crear {campo}" / mejorar: enviar prompt automático una vez.
  useEffect(() => {
    function flushPendingPrompt() {
      if (loading) return;
      const prompt = consumeRenataPendingPrompt();
      if (!prompt) return;
      void sendMessage(prompt);
    }
    const t = window.setTimeout(flushPendingPrompt, 50);
    function onOpen(ev: Event) {
      const detail = (ev as CustomEvent<{ autoPrompt?: string }>).detail;
      if (detail?.autoPrompt) {
        // Ya quedó en pendingPrompt; consumir en el próximo tick (panel puede montarse acá).
        window.setTimeout(flushPendingPrompt, 50);
        return;
      }
      flushPendingPrompt();
    }
    window.addEventListener("sm-renata-open", onOpen);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("sm-renata-open", onOpen);
    };
  }, [sendMessage, loading, conversation.id]);

  function regenerateLast() {
    const last = lastUserMsgRef.current.trim();
    if (!last) {
      toast.info("No hay un mensaje anterior para regenerar.");
      return;
    }
    if (loading) cancelGeneration();
    setMessages((prev) => {
      const copy = [...prev];
      while (copy.length > 0 && copy[copy.length - 1]?.role === "assistant") {
        copy.pop();
      }
      if (copy.length > 0 && copy[copy.length - 1]?.role === "user") {
        copy.pop();
      }
      return copy;
    });
    void sendMessage(last);
  }

  function editUserMessage(content: string) {
    setInput(content);
    window.setTimeout(() => {
      textareaRef.current?.focus();
      resizeTextarea();
    }, 0);
  }

  useEffect(() => {
    function onRegenerate() {
      regenerateLast();
    }
    function onExplain(ev: Event) {
      const detail = (ev as CustomEvent<{ content?: string }>).detail;
      const ask =
        "Explicá mejor esa respuesta" +
        (detail?.content ? `, en especial: ${detail.content.slice(0, 240)}` : "");
      void sendMessage(ask);
    }
    window.addEventListener("sm-renata-regenerate", onRegenerate);
    window.addEventListener("sm-renata-explain", onExplain);
    return () => {
      window.removeEventListener("sm-renata-regenerate", onRegenerate);
      window.removeEventListener("sm-renata-explain", onExplain);
    };
  }, [sendMessage]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage();
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    addPendingFiles(files);
    e.target.value = "";
  }

  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes("Files")) setDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setDragOver(false);
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) addPendingFiles(files);
  }

  async function transcribeVoiceBlob(blob: Blob): Promise<string | null> {
    setVoicePhase("transcribing");
    const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || "audio/webm" });
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/renata/stt", {
        method: "POST",
        credentials: "include",
        headers: clientRequestHeaders(tenantId),
        body: formData,
      });
      const data = (await res.json()) as { transcript?: string; text?: string; error?: string };
      if (!res.ok) {
        toast.error(data.error ?? "No se pudo transcribir el audio");
        return null;
      }
      const transcript = (data.transcript ?? data.text ?? "").trim();
      return transcript || null;
    } catch {
      toast.error("Error al transcribir el audio");
      return null;
    }
  }

  async function sendVoiceTranscript(blob: Blob) {
    const transcript = await transcribeVoiceBlob(blob);
    if (!transcript) {
      setVoicePhase(voiceModeRef.current ? "listening" : "idle");
      if (voiceModeRef.current) void startVoiceModeRecording();
      return;
    }
    // Solo texto: nunca adjuntar el audio al chat
    setVoicePhase("thinking");
    await sendMessage(transcript, []);
  }

  async function startVoiceModeRecording() {
    if (!voiceModeRef.current) return;
    try {
      setVoicePhase("listening");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        if (!voiceModeRef.current) {
          setVoicePhase("idle");
          return;
        }
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 800) {
          // Casi vacío — volver a escuchar
          void startVoiceModeRecording();
          return;
        }
        void sendVoiceTranscript(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      // Auto-stop after 15s silence budget
      window.setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }, 15_000);
    } catch {
      toast.error("No se pudo acceder al micrófono.");
      setIsVoiceMode(false);
      voiceModeRef.current = false;
      setVoicePhase("idle");
    }
  }

  function toggleVoiceMode() {
    const next = !isVoiceMode;
    setIsVoiceMode(next);
    voiceModeRef.current = next;
    if (next) {
      setTtsEnabled(true);
      setVoicePhase("listening");
      void fetch("/api/renata/channel-events", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ eventType: "voice_started", conversationId: conversation.id }),
      }).catch(() => {});
      void startVoiceModeRecording();
    } else {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      setVoicePhase("idle");
      void fetch("/api/renata/channel-events", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ eventType: "voice_finished", conversationId: conversation.id }),
      }).catch(() => {});
    }
  }

  async function toggleRecording() {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
      return;
    }

    try {
      setVoicePhase("listening");
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        void sendVoiceTranscript(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch {
      toast.error("No se pudo acceder al micrófono.");
    }
  }

  async function requestHandoff() {
    if (!showHandoff || handoffLoading) return;
    setHandoffLoading(true);
    try {
      const res = await fetch("/api/renata/handoff?action=request", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: conversation.id }),
      });
      if (!res.ok) throw new Error("Error al solicitar operador");
      setHandoffStatus("waiting_human");
      toast.success("Solicitud enviada. Un operador tomará la conversación en breve.");
    } catch {
      toast.error("No se pudo solicitar el handoff.");
    } finally {
      setHandoffLoading(false);
    }
  }

  async function playTTS(text: string, tid: string) {
    try {
      const truncated = text.slice(0, 500).replace(/<[^>]+>/g, "").trim();
      if (!truncated) return;
      currentAudioRef.current?.pause();
      const res = await fetch("/api/renata/tts", {
        method: "POST",
        credentials: "include",
        headers: {
          ...clientRequestHeaders(tid),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ text: truncated }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      currentAudioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch {
      // TTS opcional
    }
  }

  function handleProposalResult(
    messageId: string,
    proposal: RenataProposal,
    _approved: boolean,
    _result?: unknown,
  ) {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        return {
          ...m,
          proposals: m.proposals.map((p) => (p.id === proposal.id ? proposal : p)),
        };
      }),
    );
  }

  async function handleCancelProposal(messageId: string, proposal: RenataProposal) {
    try {
      const res = await fetch("/api/renata/action", {
        method: "POST",
        credentials: "include",
        headers: {
          ...clientRequestHeaders(tenantId),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          messageId,
          proposalId: proposal.id,
          skillId: proposal.skill_id,
          action: proposal.action,
          params: proposal.params,
          approved: false,
        }),
      });
      if (!res.ok) throw new Error("cancel failed");
      handleProposalResult(messageId, { ...proposal, status: "cancelled" }, false);
      toast.message("Propuesta cancelada");
    } catch {
      toast.error("No se pudo cancelar la propuesta.");
    }
  }

  return (
    <div
      className={cn("relative flex h-full min-h-0 flex-col", className)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-violet-500 bg-violet-500/10 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-2 text-violet-700 dark:text-violet-300">
            <Upload className="h-8 w-8" />
            <p className="text-sm font-medium">Soltá los archivos acá</p>
          </div>
        </div>
      )}

      {memoryBanner && (
        <div className="mx-4 mt-3 flex items-start gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-100">
          <Brain className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{memoryBanner}</span>
        </div>
      )}

      {/* Messages area */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-2">
            <RenataAvatar size="xl" className="h-14 w-14" />
            <p className="text-sm font-medium">Hola, soy Renata</p>
            <p className="text-xs text-muted-foreground max-w-xs">
              Tu asistente operativa de Structura. Preguntame sobre tus productos, pedidos,
              clientes o cualquier otra cosa de tu negocio.
            </p>
            {proactiveInsights.length > 0 && (
              <div className="mt-2 w-full max-w-sm rounded-xl border border-violet-200 bg-violet-50 p-3 text-left dark:border-violet-800 dark:bg-violet-950/30">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
                  <Lightbulb className="h-3.5 w-3.5" />
                  Hoy encontré {proactiveInsights.length} oportunidad{proactiveInsights.length !== 1 ? "es" : ""} para mejorar tu negocio.
                </div>
                <ul className="space-y-1.5">
                  {proactiveInsights.slice(0, 4).map((ins) => (
                    <li key={ins.id} className="flex items-start gap-1.5 text-xs text-violet-900 dark:text-violet-100">
                      {ins.severity === "critical" ? (
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-red-500" />
                      ) : (
                        <Lightbulb className="mt-0.5 h-3 w-3 shrink-0 text-amber-500" />
                      )}
                      <span className="leading-snug">{ins.title}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/app/renata/insights"
                  className="mt-2 flex items-center gap-1 text-[10px] text-violet-600 hover:underline dark:text-violet-400"
                >
                  Ver todos los insights
                  <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </div>
        )}

        {messages.map((msg) => (
          <RenataMessageBubble
            key={msg.id}
            message={msg}
            conversationId={conversation.id}
            tenantId={tenantId}
            contextIntent={contextIntent}
            onProposalResult={handleProposalResult}
            onCancelProposal={handleCancelProposal}
            onRegenerate={regenerateLast}
            onEditUserMessage={editUserMessage}
          />
        ))}

        {loading && (
          <div className="flex gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            </div>
            <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm bg-muted px-3 py-2">
              <span className="text-sm text-muted-foreground">Renata está pensando...</span>
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground underline hover:text-foreground"
                onClick={cancelGeneration}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* File pills */}
      {pendingFiles.length > 0 && (
        <div className="flex flex-wrap gap-1 px-4 pb-1">
          {pendingFiles.map((f, i) => (
            <span
              key={i}
              className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
            >
              <Paperclip className="h-2.5 w-2.5" />
              {f.name.slice(0, 20)}
              <button
                type="button"
                onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                className="ml-0.5 text-muted-foreground hover:text-foreground"
                aria-label={`Quitar archivo ${f.name}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Capture review cards */}
      {lastCaptureDocs.length > 0 && (
        <div className="shrink-0 border-t bg-muted/30 px-3 py-2 space-y-1.5">
          {lastCaptureDocs.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-2 rounded-lg border bg-background px-3 py-2"
            >
              <FileSearch className="h-4 w-4 text-violet-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{doc.filename}</p>
                <p className="text-[10px] text-muted-foreground">{doc.docClass} · {doc.confidence}</p>
              </div>
              <button
                onClick={() => {
                  setLastCaptureDocs([]);
                  void navigate({ to: `/app/renata/capture/${doc.id}` });
                }}
                className="flex items-center gap-1 text-xs text-primary font-medium hover:underline shrink-0"
              >
                <ClipboardCheck className="h-3.5 w-3.5" />
                Revisar
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input area */}
      <div className="shrink-0 border-t bg-background p-3">
        {/* Handoff banner */}
        {showHandoff && handoffStatus === "waiting_human" && (
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-xs text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950/30 dark:text-yellow-100">
            <UserCheck className="h-3.5 w-3.5 shrink-0" />
            <span>Operador solicitado — en espera de asignación.</span>
          </div>
        )}
        {/* Voice mode indicator */}
        {(isVoiceMode || voicePhase !== "idle") && (
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 dark:border-violet-800 dark:bg-violet-950/30 dark:text-violet-100">
            <Radio className="h-3.5 w-3.5 shrink-0 animate-pulse text-violet-500" />
            <span>
              {voicePhase === "listening" && "Escuchando… hablá ahora"}
              {voicePhase === "transcribing" && "Transcribiendo tu audio…"}
              {voicePhase === "thinking" && "Renata está pensando…"}
              {voicePhase === "speaking" && "Renata está respondiendo…"}
              {voicePhase === "idle" && "Modo voz activo"}
            </span>
            {isVoiceMode ? (
              <button onClick={toggleVoiceMode} className="ml-auto underline" type="button">Desactivar</button>
            ) : null}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={FILE_ACCEPT}
            className="hidden"
            onChange={handleFileChange}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            onClick={() => fileInputRef.current?.click()}
            title="Adjuntar archivo"
            aria-label="Adjuntar archivo"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8 shrink-0"
            onClick={() => cameraInputRef.current?.click()}
            title="Tomar foto"
            aria-label="Tomar foto"
          >
            <Camera className="h-4 w-4" />
          </Button>

          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              window.requestAnimationFrame(resizeTextarea);
            }}
            onKeyDown={handleKeyDown}
            placeholder={isVoiceMode ? "Modo voz activo…" : "Escribí tu consulta..."}
            rows={1}
            className="min-h-10 max-h-[40vh] flex-1 resize-none overflow-hidden py-2 text-sm leading-5"
            disabled={isVoiceMode}
          />

          {/* Continuous voice mode toggle */}
          <Button
            size="icon"
            variant={isVoiceMode ? "secondary" : "ghost"}
            className={cn(
              "h-8 w-8 shrink-0",
              isVoiceMode && "bg-violet-100 text-violet-700 hover:bg-violet-200 dark:bg-violet-900/30 dark:text-violet-300",
            )}
            onClick={toggleVoiceMode}
            title={isVoiceMode ? "Desactivar modo de voz continuo" : "Activar modo de voz continuo"}
            aria-label={isVoiceMode ? "Desactivar modo de voz continuo" : "Activar modo de voz continuo"}
          >
            <Radio className={cn("h-4 w-4", isVoiceMode && "animate-pulse")} />
          </Button>

          <Button
            size="icon"
            variant={ttsEnabled ? "secondary" : "ghost"}
            className="h-8 w-8 shrink-0"
            onClick={() => {
              if (ttsEnabled) currentAudioRef.current?.pause();
              setTtsEnabled((v) => !v);
            }}
            title={ttsEnabled ? "Desactivar voz de Renata" : "Activar voz de Renata"}
            aria-label={ttsEnabled ? "Desactivar voz de Renata" : "Activar voz de Renata"}
          >
            {ttsEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>

          <Button
            size="icon"
            variant={isRecording ? "destructive" : "ghost"}
            className="h-8 w-8 shrink-0"
            onClick={() => void toggleRecording()}
            title={isRecording ? "Detener grabación" : "Grabar audio"}
            aria-label={isRecording ? "Detener grabación" : "Grabar audio"}
            disabled={isVoiceMode}
          >
            {isRecording ? (
              <StopCircle className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>

          {showHandoff && handoffStatus === "ai" && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => void requestHandoff()}
              disabled={handoffLoading}
              title="Transferir a operador humano"
              aria-label="Transferir a operador humano"
            >
              {handoffLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
            </Button>
          )}

          {loading ? (
            <Button
              size="icon"
              variant="destructive"
              className="h-8 w-8 shrink-0"
              onClick={cancelGeneration}
              title="Cancelar generación"
              aria-label="Cancelar generación"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => void sendMessage()}
              disabled={(!input.trim() && pendingFiles.length === 0) || isVoiceMode}
              title="Enviar"
              aria-label="Enviar mensaje"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="mt-1 text-center text-[10px] text-muted-foreground">
          Renata puede cometer errores. Verificá las acciones antes de aprobarlas.
        </p>
      </div>
    </div>
  );
}
